import React, { useMemo } from "react";
import { TablePagination } from "@mui/material";
import { LedgerItem } from "../../types";
import { NavigateFunction } from "react-router-dom";
import { useUser } from "src/business-types/grocery/components/UserContext";
import { colorOptions, fontOptions } from "src/shared/modules/profile/theme/themeConstants";
import { formatDate } from "src/shared/components/dateUtils";
import { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "src/business-types/grocery/hooks/tableConfig";

interface Props {
  ledger: LedgerItem[];
  page: number;
  rowsPerPage: number;
  paginationData: any;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  navigate: NavigateFunction;
  includeHistory: boolean;
  onSortChange: (field: string) => void;
  sortField: string;
  sortOrder: "asc" | "desc";
}

const LedgerDetailTable: React.FC<Props> = ({
  ledger,
  page,
  rowsPerPage,
  paginationData,
  onPageChange,
  onRowsPerPageChange,
  navigate,
  includeHistory,
  onSortChange,
  sortField,
  sortOrder,
}) => {
  const { user, userConfigration } = useUser();
  const businessType = user?.business_type;
  const themeKey = user?.background_colour || "DodgerBlue";
  const bgClass = colorOptions[themeKey] || "bg-blue-500";
  const fontClass = fontOptions[user?.font_family || "Sans-serif"];

  const toBoolean = (value: any) => value === true || value === "true";
  const deliveryFeeEnabled = toBoolean(userConfigration?.deliveryFeeEnabled);
  const orderIdEnabled = toBoolean(userConfigration?.orderIdEnabled);
  const billNoEnabled = toBoolean(userConfigration?.billNoEnabled);
  const baseColSpan = 7;
  const colSpanCount = baseColSpan + (deliveryFeeEnabled ? 1 : 0) + (orderIdEnabled ? 1 : 0) + (billNoEnabled ? 1 : 0);


  // Map sortField from UI header to actual data key
  const fieldKeyMap: Record<string, string> = {
    orderid: "orderid",
    order_status: "order_status",
    Orderdate: "Orderdate",
    Orderamount: "Orderamount",
    discount: "discount",
    deliveryFee: "deliveryFee",
    Total_price: "Total_price",
    ReceivedAmount: "ReceivedAmount",
    CurrentTotalRemaining: "CurrentTotalRemaining",
  };

  // Flatten, sort, and paginate
  const paginatedData = useMemo(() => {
    const allOrderDetails = ledger.flatMap((entry) => {
      const orders = includeHistory ? entry.OrderDetailsHistory || [] : entry.OrderDetails || [];
      return orders.map((order) => ({ ...order, ledgerEntry: entry }));
    });

    const key = fieldKeyMap[sortField] || sortField;

    const sortedOrders = [...allOrderDetails].sort((a: any, b: any) => {
      let aVal = a[key] ?? "";
      let bVal = b[key] ?? "";

      const aNum = parseFloat(aVal);
      const bNum = parseFloat(bVal);

      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sortOrder === "asc" ? aNum - bNum : bNum - aNum;
      }

      return sortOrder === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });

    if (rowsPerPage > 0) {
      return sortedOrders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }

    return sortedOrders;
  }, [ledger, page, rowsPerPage, includeHistory, sortField, sortOrder]);

  const totalRecords = useMemo(() => {
    return ledger.reduce((total, entry) => {
      const orders = includeHistory ? entry.OrderDetailsHistory || [] : entry.OrderDetails || [];
      return total + orders.length;
    }, 0);
  }, [ledger, includeHistory]);

  const handleSort = (field: string) => {
    onSortChange(field);
  };

  const renderSortArrow = (field: string) => {
    if (sortField !== field) return null;
    return sortOrder === "asc" ? " ↑" : " ↓";
  };

  return (
    <div className={`w-[95%] mx-auto border border-gray-200 rounded-md shadow-sm ${fontClass} overflow-hidden`}>
      <div className='overflow-y-auto'>
        <table className='min-w-full text-sm border-collapse'>
          <thead className={`${bgClass} text-white`}>
            <tr>
              {orderIdEnabled && (<th className='p-4 text-left cursor-pointer' onClick={() => handleSort("orderid")}>Order ID{renderSortArrow("orderid")}</th>)}
              {billNoEnabled && (<th className='p-4 text-left cursor-pointer' onClick={() => handleSort("billNumber")}>Bill Number{renderSortArrow("billNumber")}</th>)}
              <th className='p-4 text-left cursor-pointer' onClick={() => handleSort("order_status")}>Order Status{renderSortArrow("order_status")}</th>
              <th className='p-4 text-left cursor-pointer' onClick={() => handleSort("Orderdate")}>Date & Time{renderSortArrow("Orderdate")}</th>
              <th className='p-4 text-right cursor-pointer' onClick={() => handleSort("Orderamount")}>Order Price (QAR){renderSortArrow("Orderamount")}</th>
              <th className='p-4 text-right cursor-pointer' onClick={() => handleSort("discount")}>Discount (QAR){renderSortArrow("discount")}</th>
              {deliveryFeeEnabled && (<th className='p-4 text-right cursor-pointer' onClick={() => handleSort("deliveryFee")}>Delivery Fee (QAR){renderSortArrow("deliveryFee")}</th>)}
              <th className='p-4 text-right cursor-pointer' onClick={() => handleSort("Total_price")}>Total Price (QAR){renderSortArrow("Total_price")}</th>
              <th className='p-4 text-right cursor-pointer' onClick={() => handleSort("ReceivedAmount")}>Received Price (QAR){renderSortArrow("ReceivedAmount")}</th>
              <th className='p-4 text-right cursor-pointer' onClick={() => handleSort("CurrentTotalRemaining")}>Remaining Price (QAR){renderSortArrow("CurrentTotalRemaining")}</th>
            </tr>
          </thead>

          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td colSpan={colSpanCount} className='text-center py-4 text-gray-500'>No data available</td>
              </tr>
            ) : (
              paginatedData.map((order, index) => {
                const orderAmount = parseFloat(order.Orderamount || "0");
                const discount = parseFloat(order.discount || "0");
                const deliveryFee = parseFloat(order.deliveryFee || "0");
                const totalPrice = parseFloat(order.Total_price || "0");
                const statusLower = order?.order_status?.toLowerCase();
                const isClickable = order?.orderid !== "Cash" && order?.orderid !== "Card" && statusLower !== "deleted";
                const orderIdColor = statusLower === "deleted" ? "text-gray-400" : order?.orderid === "Cash" || order?.orderid === "Card" ? "text-black" : "text-blue-600";

                return (
                  <tr key={`${page}-${index}`} className='border-t hover:bg-gray-50'>
                    {orderIdEnabled && (<td className={`p-3 text-left ${orderIdColor} ${isClickable ? "cursor-pointer hover:underline" : ""}`}
                        onClick={() => {
                          if (isClickable) {
                            if (businessType === "Laundry") navigate(`/v1/order-details/${order.orderid}`);
                            else if (businessType === "Saloon") navigate(`/v1/saloon-order-display/${order.orderid}`);
                          }
                        }}>{order.orderid}</td>)}
                    {billNoEnabled && (<td className={`p-3 text-left ${orderIdColor} ${isClickable ? "cursor-pointer hover:underline" : ""}`}
                        onClick={() => {
                          if (isClickable) {
                            if (businessType === "Laundry") navigate(`/v1/order-details/${order.orderid}`);
                            else if (businessType === "Saloon") navigate(`/v1/saloon-order-display/${order.orderid}`);
                          }
                        }}>{order.billNumber}</td>)}
                    <td className='p-3 text-left' style={{ color: order.order_status === "Cancelled" || statusLower === "deleted" ? "red" : "blue" }}>{order.order_status}</td>
                    <td className='p-4 text-left'>{formatDate(order.Orderdate)} / {order.Ordertime}</td>
                    <td className='p-4 text-right'>{orderAmount.toFixed(2)}</td>
                    <td className='p-4 text-right'>{discount.toFixed(2)}</td>
                    {deliveryFeeEnabled && (<td className='p-4 text-right'>{deliveryFee.toFixed(2)}</td>)}
                    <td className='p-4 text-right'>{totalPrice.toFixed(2)}</td>
                    <td className='p-4 text-right'>{parseFloat(order.ReceivedAmount || "0").toFixed(2)}</td>
                    <td className='p-4 text-right'>{(Number(order.CurrentTotalRemaining) || 0).toFixed(2)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className='border-t'>
        <TablePagination
          rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
          component='div'
          count={totalRecords}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      </div>
    </div>
  );
};

export default LedgerDetailTable;
